import { collabServiceCtx } from '@milkdown/plugin-collab';
import type { Ctx } from '@milkdown/kit/ctx';
import { HocuspocusProvider, WebSocketStatus } from '@hocuspocus/provider';
import { Doc } from 'yjs';

/** Stable per-tab session ID — survives editor recreation but not tab close. */
const TAB_SESSION_ID = sessionStorage.getItem('collab-session-id')
    ?? (() => {
        const id = crypto.randomUUID();
        sessionStorage.setItem('collab-session-id', id);
        return id;
    })();

export type CollabStatus = 'connecting' | 'connected' | 'disconnected' | 'auth-failed';

export interface CollabConfig {
    /** WebSocket URL for the Hocuspocus server */
    wsUrl: string;
    /** Document identifier (used as room name) */
    documentName: string;
    /**
     * Auth token or async function that returns a fresh token.
     * When a function is provided, HocuspocusProvider calls it on every
     * (re)connect, ensuring the token stays fresh beyond MSAL's ~60min lifetime.
     */
    token: string | (() => Promise<string>);
    /** Current user info for cursor awareness */
    user: {
        name: string;
        color: string;
    };
    /** Initial markdown content (applied as template if Yjs doc is empty) */
    initialContent: string;
}

export interface CollabConnection {
    provider: HocuspocusProvider;
    doc: Doc;
    destroy: () => void;
    onPeerCountChange: (callback: (count: number) => void) => () => void;
}

/**
 * Wire Yjs collaboration into a Milkdown editor instance.
 * Call this after `editor.create()` resolves, inside an `editor.action()`.
 *
 * Returns a CollabConnection that must be destroyed on unmount.
 */
export function connectCollab(
    ctx: Ctx,
    config: CollabConfig,
    onStatusChange: (status: CollabStatus, reason?: string) => void,
): CollabConnection {
    const doc = new Doc();

    onStatusChange('connecting');

    let authFailureCount = 0;
    const MAX_AUTH_FAILURES = 3;

    const provider = new HocuspocusProvider({
        url: config.wsUrl,
        name: config.documentName,
        document: doc,
        token: config.token,

        onAuthenticated() {
            authFailureCount = 0;
            onStatusChange('connected');
        },

        onAuthenticationFailed({ reason }) {
            authFailureCount++;
            onStatusChange('auth-failed', reason);
            // Let the provider retry with a fresh token call a few times before giving up.
            // This handles transient failures (e.g. token refresh racing with reconnect).
            if (authFailureCount >= MAX_AUTH_FAILURES) {
                console.warn(`collab: disconnecting after ${MAX_AUTH_FAILURES} consecutive auth failures`);
                provider.disconnect();
            }
        },

        onClose() {
            // Fires immediately when the WebSocket closes, before any
            // reconnect backoff. EditorView uses this to tear down and
            // remount the editor with fresh content without waiting.
            onStatusChange('disconnected');
        },

        onStatus({ status }) {
            if (status === WebSocketStatus.Connecting) {
                onStatusChange('connecting');
            } else if (status === WebSocketStatus.Disconnected) {
                onStatusChange('disconnected');
            }
        },
    });

    const collabService = ctx.get(collabServiceCtx);

    collabService
        .bindDoc(doc)
        .setAwareness(provider.awareness!);

    // Set local user awareness for cursor display (includes tab session ID
    // so peer count can deduplicate stale entries from the same tab)
    provider.awareness!.setLocalStateField('user', {
        name: config.user.name,
        color: config.user.color,
        sessionId: TAB_SESSION_ID,
    });

    // Wait for initial sync before activating collaboration.
    // Manually unsubscribe after first call (EventEmitter has no `once`).
    const onSynced = () => {
        provider.off('synced', onSynced);
        collabService
            .applyTemplate(config.initialContent)
            .connect();
    };
    provider.on('synced', onSynced);

    // Track peer count listener unsubscribe so destroy() cleans it up
    let peerCountUnsub: (() => void) | null = null;

    return {
        provider,
        doc,
        destroy: () => {
            peerCountUnsub?.();
            peerCountUnsub = null;
            try {
                collabService.disconnect();
            } catch {
                // Editor context may already be torn down — safe to ignore
            }
            provider.destroy();
            doc.destroy();
        },
        onPeerCountChange: (callback: (count: number) => void) => {
            const awareness = provider.awareness!;
            const handler = () => {
                const states = awareness.getStates();
                const localClientId = awareness.clientID;
                const seen = new Set<string>();
                let count = 0;
                for (const [clientId, state] of states) {
                    if (clientId === localClientId) continue;
                    const sid = (state.user as Record<string, unknown> | undefined)?.sessionId as string | undefined;
                    if (sid) {
                        if (seen.has(sid)) continue;
                        seen.add(sid);
                    }
                    count++;
                }
                callback(count);
            };
            awareness.on('change', handler);
            // Fire immediately with current count
            handler();
            peerCountUnsub = () => awareness.off('change', handler);
            return peerCountUnsub;
        },
    };
}
